import { preventDuplicateRequest } from "../utils/requestManager";
import api from "./Api";

const Case = process.env.REACT_APP_CASE_URL;

if (!Case) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables."
  );
}

const caseAPI = api(Case);

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

export const fetchEmployerGroup = async (
  stakeholderCode: any,
  isActive: any,
): Promise<any> => {
  const requestKey = `fetchEmployerGroup_${stakeholderCode}_${isActive}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/case/stakeholder/list?stakeholdertypecode=${stakeholderCode}&isActive=${isActive}`,
      );
      
      const stakeholderList = response.data?.result.map((item: any) => ({
        value: item.stakeholderId,
        label: item.stakeholderName,
      }));

      return stakeholderList;
    } catch (error) {
      console.error("Error fetching the data:", error);
      throw new Error("Unable to fetch the data. Please try again later.");
    }
  });
};

export const fetchEmployerAddress = async (
  stakeHolderID: any
): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/case/stakeholder/addresses?stakeholderid=${stakeHolderID}`
    );
    // Map the result to transform pickListItemId into value and displayName into label
    const stakeholderList = response.data?.result.map((item: any) => ({
      value: item.stakeholderaddressid,
      label: item.detailedinformation.split(" (P)")[0],
    }));

    return stakeholderList;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

export const fetchEmployerContact = async (
  stakeHolderID: any
): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/case/stakeholder/contacts?stakeholderid=${stakeHolderID}`
    );
    // Map the result to transform pickListItemId into value and displayName into label
    const stakeholderList = response.data?.result.map((item: any) => ({
      value: item.stakeholdercontactid,
      label: item.detailedinformation.split(" (E)")[0],
    }));

    return stakeholderList;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

export const fetchSubjectInfo = async (caseID: any): Promise<any> => {
  const requestKey = `fetchSubjectInfo_${caseID}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/case/subject/details?caseInfoId=${caseID}`,
      );
      
      return response.data?.result;
    } catch (error) {
      console.error("Error fetching the data:", error);
      throw new Error("Unable to fetch the data. Please try again later.");
    }
  });
};

export const addCaseSubject = async (
  subjectData: any
): Promise<ApiResponse<any>> => {
  try {
    console.log(subjectData, "subjectData");

    const response = await caseAPI.post<ApiResponse<any>>(
      "/api/case/subject/add",
      subjectData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again."
    );
  }
};

export const updateCaseSubject = async (
  subjectData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.put<ApiResponse<any>>(
      "/api/case/subject/update",
      subjectData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again."
    );
  }
};

export const GetSubjectAddressList = async (
  stakeholderid: number
): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/case/subject/address/list?caseSubjectId=${stakeholderid}`
    );
    const updatedResult = response.data?.result.map((address: any) => ({
      ...address,
      actions: { Edit: "Edit" }, // Adding Edit action inside each address entry
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error fetching the address list:", error);
    throw new Error("Unable to fetch address data. Please try again later.");
  }
};

export const addCaseSubjectAddress = async (
  subjectAddressData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.post<ApiResponse<any>>(
      "/api/case/subject/address/add",
      subjectAddressData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again."
    );
  }
};

export const updateCaseSubjectAddress = async (
  subjectData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.put<ApiResponse<any>>(
      "/api/case/subject/address/update",
      subjectData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again."
    );
  }
};
export const searchCaseSubjectByText = async (
  searchText: string,
): Promise<any[]> => {
  try {
    const response = await caseAPI.get(
      `/api/case/subject/search?searchText=${searchText}`
    );
    return response.data?.result || [];
  } catch (error) {
    console.error("Error searching case subject:", error);
    throw new Error(
      "Unable to fetch subject search results. Please try again later."
    );
  }
};
export const searchCaseSubjectByTextWithQMEDoctorWiseList = async (
  searchText: string,
  qmeDoctorId: number,
): Promise<any[]> => {
  try {
    const response = await caseAPI.get(
      `/api/case/subject/search?searchText=${searchText}&qmeDoctorContactId=${qmeDoctorId}`
    );
    return response.data?.result || [];
  } catch (error) {
    console.error("Error searching case subject:", error);
    throw new Error(
      "Unable to fetch subject search results. Please try again later."
    );
  }
};
export const createCaseFromSubject = async (
  subjectId: number
): Promise<any> => {
  try {
    const response = await caseAPI.post(
      `/api/case/information/new/from-subject?subjectId=${subjectId}`
    );

    return response.data?.result || {};
  } catch (error) {
    console.error("Error creating case from subject:", error);
    throw new Error("Unable to create case. Please try again later.");
  }
};
